import { formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CustomerRevenueOverviewParams,
    ProductGroupMonthlyRevenue,
} from '../../../services'
import {
    ColumnTotals,
    useCustomerRevenueOverview,
} from '../../../hooks/use-customer-revenue-overview'
import Paper from '@/components/Paper'

interface OverviewTableProps {
    filters: CustomerRevenueOverviewParams
}

const formatPrice = (value: number) => {
    if (!value || value === 0) return '-'
    return formatNumeral(value, 0)
}

const formatPercent = (value: number) => {
    if (!value || value === 0) return '0.00%'
    return `${formatNumeral(value, 2)}%`
}

// -- Sub-components --

const OverviewLegend: React.FC<{ t: (key: string) => string }> = ({ t }) => (
    <div className="flex flex-wrap items-center gap-4 text-xs">
        <span className="text-slate-600 font-medium">
            {t('overview.legend')}:
        </span>
        {[
            {
                color: 'bg-purple-100 border-purple-300',
                label: t('overview.pb_previous_year'),
            },
            {
                color: 'bg-blue-100 border-blue-300',
                label: t('overview.pb_current_year'),
            },
            {
                color: 'bg-orange-100 border-orange-300',
                label: `KPB: ${t('overview.kpb_full')}`,
            },
            {
                color: 'bg-green-100 border-green-300',
                label: t('overview.allocated_by_type'),
            },
            {
                color: 'bg-amber-100 border-amber-300',
                label: t('overview.unallocated_by_type'),
            },
            {
                color: 'bg-indigo-100 border-indigo-300',
                label: t('overview.total_by_type'),
            },
            {
                color: 'bg-emerald-100 border-emerald-300',
                label: t('overview.total_summary'),
            },
        ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
                <span className={`inline-block w-4 h-4 rounded ${color}`} />
                <span className="text-slate-700">{label}</span>
            </div>
        ))}
    </div>
)

const OverviewTableHeader: React.FC<{
    months: string[]
    t: (key: string) => string
}> = ({ months, t }) => (
    <thead>
        {/* Main header row */}
        <tr>
            <th
                rowSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-left font-medium sticky left-0 bg-slate-100 z-10 min-w-[50px] text-slate-700"
            >
                STT
            </th>
            <th
                rowSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-left font-medium sticky left-[50px] bg-slate-100 z-10 min-w-[150px] text-slate-700"
            >
                {t('overview.service')}
            </th>
            <th
                colSpan={months.length * 3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-blue-100 text-blue-800"
            >
                {t('overview.monthly_revenue')}
            </th>
            <th
                colSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-green-100 text-green-800"
            >
                {t('overview.allocated_by_type')}
            </th>
            <th
                colSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-amber-100 text-amber-800"
            >
                {t('overview.unallocated_by_type')}
            </th>
            <th
                colSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-indigo-100 text-indigo-800"
            >
                {t('overview.total_by_type')}
            </th>
            <th
                rowSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-emerald-100 text-emerald-800 min-w-[120px]"
            >
                {t('overview.total_revenue')}
            </th>
            <th
                rowSpan={3}
                className="border border-gray-300 px-3 py-2.5 text-center font-medium bg-emerald-100 text-emerald-800 min-w-[80px]"
            >
                {t('overview.percentage')}
            </th>
        </tr>
        {/* Second header row - Month names + type sub-headers */}
        <tr>
            {months.map((month) => (
                <th
                    key={month}
                    colSpan={3}
                    className="border border-gray-300 px-2 py-2 text-center font-medium text-xs bg-blue-50 text-blue-700"
                >
                    {dayjs(month).format('MM/YYYY')}
                </th>
            ))}
            {/* Allocated by type */}
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-green-50 text-green-700"
            >
                {t('payment_type.renewal')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-green-50 text-green-700"
            >
                {t('payment_type.new_sale')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-green-50 text-green-700"
            >
                {t('payment_type.upsell')}
            </th>
            {/* Unallocated by type */}
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-amber-50 text-amber-700"
            >
                {t('payment_type.renewal')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-amber-50 text-amber-700"
            >
                {t('payment_type.new_sale')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-amber-50 text-amber-700"
            >
                {t('payment_type.upsell')}
            </th>
            {/* Summary by type */}
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-indigo-50 text-indigo-700"
            >
                {t('payment_type.renewal')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-indigo-50 text-indigo-700"
            >
                {t('payment_type.new_sale')}
            </th>
            <th
                rowSpan={2}
                className="border border-gray-300 px-2 py-2 text-center font-medium text-xs min-w-[90px] bg-indigo-50 text-indigo-700"
            >
                {t('payment_type.upsell')}
            </th>
        </tr>
        {/* Third header row - PB trước / PB nay / KPB sub-columns */}
        <tr>
            {months.map((month) => (
                <Fragment key={month}>
                    <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[80px] bg-purple-50 text-purple-600">
                        {t('overview.pb_prev_short')}
                    </th>
                    <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[80px] bg-blue-50/70 text-blue-600">
                        {t('overview.pb_curr_short')}
                    </th>
                    <th className="border border-gray-300 px-2 py-1.5 text-center font-medium text-xs min-w-[80px] bg-orange-50 text-orange-600">
                        KPB
                    </th>
                </Fragment>
            ))}
        </tr>
    </thead>
)

const OverviewTableRow: React.FC<{
    group: ProductGroupMonthlyRevenue
    index: number
    months: string[]
    grandTotal: number
}> = ({ group, index, months, grandTotal }) => {
    const percentage =
        grandTotal > 0 ? (group.total_revenue / grandTotal) * 100 : 0

    return (
        <tr className="hover:bg-slate-50 transition-colors">
            <td className="border border-gray-300 px-3 py-2 text-center sticky left-0 bg-white">
                {index + 1}
            </td>
            <td className="border border-gray-300 px-3 py-2 sticky left-[50px] bg-white">
                {group.product_group_name}
            </td>
            {/* Monthly values */}
            {months.map((month) => {
                const monthData = group.monthly_data[month]
                return (
                    <Fragment key={month}>
                        <td className="border border-gray-300 px-3 py-2 text-right bg-purple-50/30">
                            {formatPrice(
                                monthData?.allocated_previous_year || 0
                            )}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right">
                            {formatPrice(
                                monthData?.allocated_current_year || 0
                            )}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-right bg-orange-50/30">
                            {formatPrice(monthData?.unallocated || 0)}
                        </td>
                    </Fragment>
                )
            })}
            {/* Allocated by type */}
            <td className="border border-gray-300 px-3 py-2 text-right bg-green-50/50">
                {formatPrice(group.allocated_renewal)}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right bg-green-50/50">
                {formatPrice(group.allocated_new_sale)}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right bg-green-50/50">
                {formatPrice(group.allocated_upsell)}
            </td>
            {/* Unallocated by type */}
            <td className="border border-gray-300 px-3 py-2 text-right bg-amber-50/50">
                {formatPrice(group.unallocated_renewal)}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right bg-amber-50/50">
                {formatPrice(group.unallocated_new_sale)}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right bg-amber-50/50">
                {formatPrice(group.unallocated_upsell)}
            </td>
            {/* Summary by type (allocated + unallocated) */}
            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-indigo-50/50 text-indigo-700">
                {formatPrice(
                    group.allocated_renewal + group.unallocated_renewal
                )}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-indigo-50/50 text-indigo-700">
                {formatPrice(
                    group.allocated_new_sale + group.unallocated_new_sale
                )}
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-indigo-50/50 text-indigo-700">
                {formatPrice(group.allocated_upsell + group.unallocated_upsell)}
            </td>
            {/* Total */}
            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-emerald-50/50 text-emerald-700">
                {formatPrice(group.total_revenue)}
            </td>
            {/* Percentage */}
            <td className="border border-gray-300 px-3 py-2 text-right font-medium bg-emerald-50/50 text-emerald-700">
                {formatPercent(percentage)}
            </td>
        </tr>
    )
}

const OverviewTableTotalRow: React.FC<{
    months: string[]
    columnTotals: ColumnTotals
    grandTotal: number
    t: (key: string) => string
}> = ({ months, columnTotals, grandTotal, t }) => (
    <tr className="bg-slate-200 font-bold">
        <td
            colSpan={2}
            className="border border-gray-300 px-3 py-2.5 text-center sticky left-0 bg-slate-200 text-slate-800 font-bold"
        >
            {t('overview.total')}
        </td>
        {/* Monthly totals */}
        {months.map((month) => (
            <Fragment key={month}>
                <td className="border border-gray-300 px-3 py-2.5 text-right bg-purple-100/50 text-purple-800">
                    {formatPrice(
                        columnTotals.monthly[month]?.allocated_previous_year ||
                            0
                    )}
                </td>
                <td className="border border-gray-300 px-3 py-2.5 text-right bg-blue-100/50 text-blue-800">
                    {formatPrice(
                        columnTotals.monthly[month]?.allocated_current_year || 0
                    )}
                </td>
                <td className="border border-gray-300 px-3 py-2.5 text-right bg-orange-100/50 text-orange-800">
                    {formatPrice(columnTotals.monthly[month]?.unallocated || 0)}
                </td>
            </Fragment>
        ))}
        {/* Allocated by type totals */}
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-green-100 text-green-800">
            {formatPrice(columnTotals.allocatedRenewal)}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-green-100 text-green-800">
            {formatPrice(columnTotals.allocatedNewSale)}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-green-100 text-green-800">
            {formatPrice(columnTotals.allocatedUpsell)}
        </td>
        {/* Unallocated by type totals */}
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-amber-100 text-amber-800">
            {formatPrice(columnTotals.unallocatedRenewal)}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-amber-100 text-amber-800">
            {formatPrice(columnTotals.unallocatedNewSale)}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-amber-100 text-amber-800">
            {formatPrice(columnTotals.unallocatedUpsell)}
        </td>
        {/* Summary by type totals */}
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-indigo-100 text-indigo-800">
            {formatPrice(
                columnTotals.allocatedRenewal + columnTotals.unallocatedRenewal
            )}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-indigo-100 text-indigo-800">
            {formatPrice(
                columnTotals.allocatedNewSale + columnTotals.unallocatedNewSale
            )}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-indigo-100 text-indigo-800">
            {formatPrice(
                columnTotals.allocatedUpsell + columnTotals.unallocatedUpsell
            )}
        </td>
        {/* Grand total */}
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-emerald-100 text-emerald-800">
            {formatPrice(grandTotal)}
        </td>
        <td className="border border-gray-300 px-3 py-2.5 text-right bg-emerald-100 text-emerald-800">
            100.00%
        </td>
    </tr>
)

// -- Main Component --

const OverviewTable: React.FC<OverviewTableProps> = ({ filters }) => {
    const { t } = useTranslation('report')
    const { productGroups, months, grandTotal, columnTotals, isLoading } =
        useCustomerRevenueOverview(filters)

    if (isLoading) {
        return (
            <div className="space-y-2">
                <div className="h-8 w-full bg-gray-200 animate-pulse rounded" />
                <div className="h-64 w-full bg-gray-200 animate-pulse rounded" />
            </div>
        )
    }

    if (!productGroups.length || !months.length) {
        return (
            <div className="text-center py-8 text-gray-500 bg-white border rounded-sm">
                {t('no_data')}
            </div>
        )
    }

    return (
        <Paper className="space-y-2">
            <OverviewLegend t={t} />

            <div className="border border-gray-300 rounded-md overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                        <OverviewTableHeader months={months} t={t} />
                        <tbody>
                            {productGroups.map((group, index) => (
                                <OverviewTableRow
                                    key={group.product_group_name}
                                    group={group}
                                    index={index}
                                    months={months}
                                    grandTotal={grandTotal}
                                />
                            ))}
                            <OverviewTableTotalRow
                                months={months}
                                columnTotals={columnTotals}
                                grandTotal={grandTotal}
                                t={t}
                            />
                        </tbody>
                    </table>
                </div>
            </div>
        </Paper>
    )
}

export default OverviewTable
